import React, { useState } from "react";
import { useQueryClient } from "react-query";
import { Trans, useTranslation } from "react-i18next";

import { Modal } from "components/Modal/Modal";
import TextInput from "components/inputs/TextInput";
import RadioInput from "components/inputs/RadioInput";
import TextArea from "components/inputs/TextAreaInput";
import { useUpdateProjectPayload } from "hooks/useProjects";
import { Feature, useFeatureFlags } from "context/FeatureFlag";
import { PrimaryButton } from "components/Button/PrimaryButton";
import SearchSelectInput from "components/inputs/SearchSelectInput";
import { useProjectDetails } from "pages/Details/hooks/useProjectDetails";
import { useRiskGroupingTemplates } from "./hooks/useRiskGroupingTemplates";
import { useRiskTemplates as useCustomRiskTemplates } from "components/RiskTemplates/hooks/useRiskTemplates";

interface CreateRiskGrouppingOnDashboardProps {
  projectCRN: string;
  button: React.FunctionComponent<React.ButtonHTMLAttributes<any>>;
}

export const CreateRiskGrouppingOnDashboard: React.FunctionComponent<
  CreateRiskGrouppingOnDashboardProps
> = (props) => {
  const [modalVisible, setModalVisible] = React.useState(false);

  return (
    <>
      <div>
        <props.button onClick={() => setModalVisible(true)} />
      </div>

      <>
        <Modal
          onClose={() => setModalVisible(false)}
          show={modalVisible}
          width="1/2"
          content={
            <CreateRiskGrouppingForm
              projectCRN={props.projectCRN}
              onClose={() => setModalVisible(false)}
            />
          }
        />
      </>
    </>
  );
};

interface CreateRiskFormProps {
  projectCRN: string;
  onClose: () => void;
}

enum CreateOption {
  scratch = "scratch",
  template = "template",
}

const CreateRiskGrouppingForm = (props: CreateRiskFormProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { isFeatureEnabled } = useFeatureFlags();
  const { project } = useProjectDetails(props.projectCRN);
  const { updateProject, isLoading } = useUpdateProjectPayload(
    props.projectCRN,
    t("notification.saving.create_chapter_from_template_success")
  );

  const { templates } = useRiskGroupingTemplates();
  const { riskTemplates } = useCustomRiskTemplates();

  const [selectedOption, setSelectedOption] = useState<CreateOption>(
    CreateOption.scratch
  );
  const [payload, setPayload] = React.useState({
    name: "",
    description: "",
    templateId: "",
    members: [] as string[],
  });

  const handleCreateRiskGroupping = async () => {
    const risks = project?.risks || [];
    const addedRiskIds = [];

    if (payload.templateId) {
      const selectedRiskTemplates = riskTemplates?.filter((rt) =>
        payload.members.includes(rt.crn)
      );

      const riskTemplatePayloads = selectedRiskTemplates?.map((rt) => ({
        ...rt.payload,
        crn: undefined,
        resource_id: generateId(28),
      }));

      const risksFromTemplate = riskTemplatePayloads || [];

      risks.push(...risksFromTemplate);
      addedRiskIds.push(...risksFromTemplate.map((r) => r.resource_id));
    }

    updateProject({
      ...project!,
      risks: risks,
      riskGrouping: [
        ...(project!.riskGrouping || []),
        {
          id: generateId(28),
          description: payload.description,
          label: payload.name,
          members: addedRiskIds,
        },
      ],
    });

    queryClient.refetchQueries(["riskTemplates"]);
    queryClient.refetchQueries(["riskGroupingTemplates"]);

    props.onClose();
  };

  const handleTemplateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const templateId = e.target.value;
    const selectedTemplate = templates.find((t) => t.id === templateId);

    if (selectedTemplate) {
      setPayload({
        name: selectedTemplate.label,
        description: selectedTemplate.description,
        templateId: selectedTemplate.id,
        members: selectedTemplate.members,
      });
    } else {
      setPayload({
        name: "",
        description: "",
        templateId: "",
        members: [],
      });
    }
  };

  const options = [
    {
      label: t("risks.createGroupping.options.scratch"),
      value: CreateOption.scratch,
    },
    isFeatureEnabled(Feature.ChapterTemplates)
      ? {
          label: t("risks.createGroupping.options.template"),
          value: CreateOption.template,
        }
      : null,
  ].filter((o) => !!o);

  return (
    <div>
      <span className="text-xl">{t("risks.createGroupping.headline")}</span>
      <div className="text-sm text-gray-500 pt-4">
        <Trans i18nKey="risks.createGroupping.subline">
          <p>
            Mit einer Gefährdungsgruppe können Sie Gefährdungen logisch
            gruppieren und so einen besseren Überblick behalten.
          </p>
          <p>
            Sie können Gefährdungen aus der Standard-Gruppe "Gefährdungen" in
            Ihre selbsterstellten Gruppen per{" "}
            <span className="font-bold">"Drag and Drop"</span> hin und her
            ziehen, um ihre Zuordnung und Reihenfolge zu ändern.
          </p>
        </Trans>
      </div>
      <div className="pt-4 pb-4">
        {isFeatureEnabled(Feature.ChapterTemplates) && (
          <RadioInput
            islast
            required
            id="useTemplate"
            name="useTemplate"
            label={t("risks.createGroupping.create_choice")}
            defaultValue={selectedOption}
            onBlur={(option) =>
              setSelectedOption(option.target.value as CreateOption)
            }
            options={options}
          />
        )}

        {selectedOption === CreateOption.template &&
          isFeatureEnabled(Feature.ChapterTemplates) && (
            <div className="pb-2">
              <SearchSelectInput
                required
                id="template"
                name="template"
                label={t("risks.createGroupping.template")}
                defaultValue={
                  templates.find((t) => t.id === payload.templateId)?.label
                }
                options={templates.map((template) => ({
                  label: template.label,
                  value: template.id,
                }))}
                onBlur={handleTemplateChange}
              />
            </div>
          )}

        <TextInput
          required
          uncontrolled
          id="groupping"
          data-test="create-groupping-name"
          label={t("risks.createGroupping.groupName")}
          defaultValue={payload.name}
          // @ts-ignore
          onChange={(e) => {
            setPayload((p) => ({ ...p, name: e.target.value }));
          }}
          placeholder={t("risks.createGroupping.placeholder")}
          onKeyDown={(evt: React.KeyboardEvent<HTMLInputElement>) => {
            if (evt.key === "Enter") {
              payload.name && handleCreateRiskGroupping();
            }
          }}
        />

        <TextArea
          required
          uncontrolled
          id="groupping_description"
          name="groupping_description"
          data-test="create-groupping-description"
          label={t("risks.createGroupping.description")}
          value={payload.description}
          // @ts-ignore
          onChange={(e) => {
            setPayload((p) => ({ ...p, description: e.target.value }));
          }}
          placeholder={t("risks.createGroupping.placeholder")}
          onKeyDown={(evt: React.KeyboardEvent<HTMLInputElement>) => {
            if (evt.key === "Enter") {
              payload.name && handleCreateRiskGroupping();
            }
          }}
        />
      </div>

      <div className="text-right flex justify-center items-center float-right">
        <a
          className="px-6 text-sm text-gray-500 hover:text-gray-700 cursor-pointer"
          onClick={() => props.onClose()}
        >
          {t("risks.group.cancel")}
        </a>

        <PrimaryButton
          float="right"
          disabled={
            !payload ||
            isLoading ||
            (selectedOption === CreateOption.template && !payload.templateId)
          }
          onClick={handleCreateRiskGroupping}
        >
          {isLoading
            ? t("risks.createGroupping.cta_loading")
            : t("risks.createGroupping.cta")}
        </PrimaryButton>
      </div>
    </div>
  );
};

function generateId(length: number = 8): string {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}
